import { formatDate, getTypeof } from './utils'

type DT = number | string | Date
interface IConfig { offset?: number, format?: string, dt?: DT, date?: number }

const MS_OF_HOUR: number = 3600000
export const getTimeOffset = (regionId?: number): number => {
  const regionList = useCountrys().value || []
  const storeRegionId = regionId || useCurrentCountry().value || 6
  const storeRegionTimeZone = regionList
    .find((c: any) => c.id === storeRegionId)?.utcTimeOffset

  return storeRegionTimeZone as number
}

/**
 * 0时区转本地时区
 * @param {Date | string} date
 * @param {Object} config {
 *  offset: 偏移量(单位：h，默认为当前国家时区偏移量)
 *  format：默认 yyyy-MM-dd hh:mm:ss.SZ
 * }
 */
// export
export const zeroToLocalZonetime = (date: DT, config: IConfig = {}): string => {
  if (!date) {
    return ''
  }
  if (['Number', 'String'].includes(getTypeof(date))) {
    date = new Date(date)

    if (date.toString() === 'Invalid Date') {
      return ''
    }
  }

  const dt = date as Date
  const timeOffset = getTimeOffset()
  const currentZonetimeOffset = (config.offset || timeOffset) * MS_OF_HOUR
  // 转换为0时区 + 当前时区偏移量
  const current = dt.getTime()
    + (dt.getTimezoneOffset() * 60 * 1000)
    + currentZonetimeOffset

  return formatDate(new Date(current), config.format || 'yyyy-MM-dd hh:mm:ss')
}

/**
 * 本地时区转0时区
 * @param {Date | string} date
 * @param {string} format default 'yyyy-MM-ddThh:mm:ss.SZ'
 */
export const localToZeroZonetime = (date: DT, format = 'yyyy-MM-ddThh:mm:ss.SZ') => {
  if (!date) {
    return
  }

  let isZero = 1
   if (getTypeof(date) === 'String') {
    isZero = /Z$/.test(date.toString()) ? -1 : 1
    date = new Date(date)

    if (date.toString() === 'Invalid Date') {
      return ''
    }
  }

  if (['Number', 'String'].includes(getTypeof(date))) {
    date = new Date(date)

    if (date.toString() === 'Invalid Date') {
      return
    }
  }
  const dt = date as Date
  const current = dt.getTime() + (dt.getTimezoneOffset() * 60 * 1000) + (getTimeOffset() * MS_OF_HOUR * isZero)
  return formatDate(new Date(current), format)
}

/**
 * 获取指定日期开始时间和结束时间
 * @param {num} type 1 开始时间   2  结束时间
 * @param {*} config { format: 格式化日期, date：指定日期 或者 指定偏移天数, offset：时区偏移量 }
 * @returns
 */
 export const getEndOrStartTimeByTime = (type: number = 1, config: IConfig = {}) => {
  const dt: Date = (config.dt || new Date()) as Date

  if (config.date && getTypeof(config.date) === 'Number') {
    dt.setDate(dt.getDate() + config.date)
  }
  const date = formatDate(dt, 'yyyy-MM-dd')
  const hs = type === 1 ? '00:00:00.000' : '23:59:59.999'
  const newDt = new Date(`${date}T${hs}Z`)

  const current = newDt.getTime()
    + (dt.getTimezoneOffset() * 60 * 1000)
    - ((config.offset || getTimeOffset()) * MS_OF_HOUR)

  const t = formatDate(new Date(current), config.format || `yyyy-MM-ddThh:mm:ss.SZ`)

  return t
}

export const monthToEn = (month: number) => {
  const monthEn: IAnyObject = {
    1: 'January',
    2: 'February',
    3: 'March',
    4: 'April',
    5: 'May',
    6: 'June',
    7: 'July',
    8: 'August',
    9: 'September',
    10:'October',
    11:'November',
    12:'December',
  }
  return monthEn[month]
}


/**
 * yyyy-MM-dd -> MM.dd  2024--05-06 -> May.6th
 * @param dateString yyyy-MM-dd
 * @returns 
 */
export function convertDateToMonthFormat(dateString: string) {
  // 月份名称数组
  const months = ["Jan.", "Feb.", "Mar.", "Apr.", "May.", "Jun.", "Jul.", "Aug.", "Sep.", "Oct.", "Nov.", "Dec."];

  // 将日期字符串分割为数组
  const [year, month, day] = dateString.split('-');

  // 获取月份名称
  const monthName = months[parseInt(month, 10) - 1];

  // 获取日期后缀
  const dayInt = parseInt(day, 10);
  let daySuffix = 'th';
  if (dayInt === 1 || dayInt === 21 || dayInt === 31) {
      daySuffix = 'st';
  } else if (dayInt === 2 || dayInt === 22) {
      daySuffix = 'nd';
  } else if (dayInt === 3 || dayInt === 23) {
      daySuffix = 'rd';
  }

  // 拼接最终的日期格式
  return {
    format: `${monthName}${dayInt}${daySuffix}`,
    month: monthName,
    day: `${dayInt}${daySuffix}`
  }
}
/**
 * 返回 月份区间：May.8th~Jun.7th
 * @param startTime 
 * @param endTime 
 */
export const timeToMonthRange = (startTime: Date, endTime: Date) => {
  const startDate = zeroToLocalZonetime(startTime, { format: 'yyyy-MM-dd' })
  const endDate = zeroToLocalZonetime(endTime, { format: 'yyyy-MM-dd' })

  const startMonth = convertDateToMonthFormat(startDate)
  const endMonth = convertDateToMonthFormat(endDate)

  // 月份相同的情况，返回类似：May.1st~22nd
  if(startMonth.month === endMonth.month) {
    return `${startMonth.format}~${endMonth.day}`
  }

  // May.1st~Jun.7th
  return `${startMonth.format}~${endMonth.format}`
}

/**
 * 转英文日期显示
 * @param {正常时间} type : 1/年月日时分秒， 2/年月日
 * @returns
 */
export function dateFormatEnFn(d: Date, type = 1) {
  const i = zeroToLocalZonetime(d)
  if (!i) {
    return i
  }
  if (type === 2) {
    return (
      `${
        new Date(i).getDate()
      }
      ${new Date(i).toDateString()
        .split(' ')[1]
      }, ${
        new Date(i).getFullYear()
      }`
    )
  } else {
    return (
      `${
        new Date(i).getDate()
      }
      ${new Date(i).toDateString()
        .split(' ')[1]
      }, ${
        new Date(i).getFullYear()
      } ${
        new Date(i).getHours() <= 9 ? `0${new Date(i).getHours()}` : new Date(i).getHours()
      }:${
        new Date(i).getMinutes() <= 9 ? `0${new Date(i).getMinutes()}` : new Date(i).getMinutes()}`
    )
  }
  
}

export default defineNuxtPlugin(async (NuxtApp) => {
  const provide = { 
    tz: {
      zeroToLocalZonetime,
      getTimeOffset,
      localToZeroZonetime,
      getEndOrStartTimeByTime,
      monthToEn,
      timeToMonthRange,
      dateFormatEnFn
    }
  }

  return { provide }
})
